/* Netalico WebMCP tools runtime.
 *
 * Reads inline JSON data emitted by snippets/custom-webmcp-tools.liquid and
 * registers structured tools via navigator.modelContext.registerTool so that
 * WebMCP-aware AI agents (Chrome EPP, or Claude/ChatGPT through a bridge) can
 * read services and case studies, book an intro call, or submit a structured
 * proposal request without DOM scraping.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 *
 * Kill switch: set window.NETALICO_WEBMCP_DISABLE = true before this script
 * runs to skip registration.
 */
(function () {
  if (window.NETALICO_WEBMCP_DISABLE) return;
  if (window.__netalicoWebMCPInstalled) return;

  var dataEl = document.getElementById('netalico-webmcp-data');
  if (!dataEl) return;

  var DATA;
  try {
    DATA = JSON.parse(dataEl.textContent || '{}');
  } catch (err) {
    if (window.console) console.warn('[netalico-webmcp] invalid data block', err);
    return;
  }

  window.__netalicoWebMCPInstalled = true;

  function findCaseStudy(handle) {
    var needle = String(handle || '').toLowerCase();
    var list = DATA.case_studies || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].handle).toLowerCase() === needle) return list[i];
    }
    return null;
  }

  function navigateWithGesture(client, url) {
    var go = function () {
      window.location.assign(url);
      return { navigated: true, url: url };
    };
    if (client && typeof client.requestUserInteraction === 'function') {
      return client.requestUserInteraction(function () { return Promise.resolve(go()); });
    }
    return Promise.resolve(go());
  }

  function buildTools() {
    return [
      {
        name: 'list_services',
        title: 'List Netalico services',
        description: 'Returns the services Netalico offers, each with a short summary and a link.',
        annotations: { readOnlyHint: true },
        inputSchema: { type: 'object', properties: {} },
        execute: function () {
          return Promise.resolve({ services: DATA.services || [] });
        }
      },
      {
        name: 'list_case_studies',
        title: 'List Netalico case studies',
        description: 'Returns published portfolio case studies. Use the optional tag filter to narrow by platform, vertical, or scope.',
        annotations: { readOnlyHint: true },
        inputSchema: {
          type: 'object',
          properties: {
            tag: {
              type: 'string',
              description: 'Optional case-insensitive substring matched against article tags (e.g. "migration", "b2b", "klaviyo").'
            },
            limit: {
              type: 'integer',
              description: 'Maximum number of results to return (default 25, max 30).',
              minimum: 1,
              maximum: 30
            }
          }
        },
        execute: function (input) {
          var tag = (input && input.tag ? String(input.tag) : '').toLowerCase();
          var limit = (input && input.limit) ? Math.min(30, Math.max(1, input.limit | 0)) : 25;
          var results = DATA.case_studies || [];
          if (tag) {
            results = results.filter(function (c) {
              return (c.tags || []).some(function (t) {
                return String(t).toLowerCase().indexOf(tag) !== -1;
              });
            });
          }
          return Promise.resolve({
            count: results.length,
            case_studies: results.slice(0, limit).map(function (c) {
              return { handle: c.handle, title: c.title, url: c.url, tags: c.tags, published_at: c.published_at };
            })
          });
        }
      },
      {
        name: 'get_case_study',
        title: 'Get a Netalico case study',
        description: 'Returns full details for one case study by its handle (slug). Use list_case_studies to discover handles.',
        annotations: { readOnlyHint: true },
        inputSchema: {
          type: 'object',
          properties: {
            handle: {
              type: 'string',
              description: 'Article handle exactly as returned by list_case_studies (e.g. "portfolio/the-country-butcher"). Pass the value through as-is.'
            }
          },
          required: ['handle']
        },
        execute: function (input) {
          var match = findCaseStudy(input && input.handle);
          if (!match) return Promise.resolve({ found: false });
          return Promise.resolve({ found: true, case_study: match });
        }
      },
      {
        name: 'book_intro_call',
        title: 'Book an intro call with Netalico',
        description: 'Opens the Netalico Pipedrive scheduler in the current tab so the visitor can pick a time. Use only when the visitor has confirmed they want to book.',
        inputSchema: {
          type: 'object',
          properties: {
            topic: {
              type: 'string',
              description: 'Optional short topic for the call (e.g. "BigCommerce to Shopify migration").'
            }
          }
        },
        execute: function (_input, client) {
          return navigateWithGesture(client, DATA.booking_url);
        }
      },
      {
        name: 'request_proposal',
        title: 'Request a Netalico proposal',
        description: 'Captures a structured project inquiry (company, platform, scope, budget, timeline) and takes the visitor to the contact page to complete the Pipedrive intake form. Use only with the visitor\'s confirmation.',
        inputSchema: {
          type: 'object',
          properties: {
            name: { type: 'string', description: 'Contact full name.' },
            email: { type: 'string', format: 'email', description: 'Contact email.' },
            company: { type: 'string', description: 'Company name.' },
            role: { type: 'string', description: 'Role/title at the company.' },
            current_platform: {
              type: 'string',
              description: 'Current commerce platform.',
              enum: ['shopify', 'shopify_plus', 'magento', 'bigcommerce', 'woocommerce', 'salesforce_commerce_cloud', 'custom', 'other', 'none']
            },
            project_type: {
              type: 'string',
              description: 'What kind of engagement.',
              enum: ['migration', 'new_build', 'redesign', 'cro', 'b2b', 'app_or_integration', 'retainer', 'other']
            },
            budget_range: {
              type: 'string',
              description: 'Budget range in USD.',
              enum: ['under_25k', '25k_50k', '50k_100k', '100k_250k', '250k_plus', 'unsure']
            },
            timeline: {
              type: 'string',
              description: 'Desired timeline.',
              enum: ['asap', '1_3_months', '3_6_months', '6_plus_months', 'exploratory']
            },
            notes: { type: 'string', description: 'Anything else worth knowing about the project.' }
          },
          required: ['name', 'email', 'company', 'project_type']
        },
        execute: function (input, client) {
          var captured = {
            captured_at: new Date().toISOString(),
            referrer: location.href,
            payload: input || {}
          };
          try { sessionStorage.setItem('netalico.proposal_intent', JSON.stringify(captured)); } catch (e) {}

          var go = function () {
            window.location.assign(DATA.contact_url);
            return { navigated: true, url: DATA.contact_url, captured: captured };
          };
          if (client && typeof client.requestUserInteraction === 'function') {
            return client.requestUserInteraction(function () { return Promise.resolve(go()); });
          }
          return Promise.resolve(go());
        }
      }
    ];
  }

  function register() {
    if (!navigator || !navigator.modelContext || typeof navigator.modelContext.registerTool !== 'function') {
      return false;
    }
    var tools = buildTools();
    for (var i = 0; i < tools.length; i++) {
      try {
        navigator.modelContext.registerTool(tools[i]);
      } catch (err) {
        if (window.console) console.warn('[netalico-webmcp] failed to register', tools[i].name, err);
      }
    }
    if (window.console) {
      console.info('[netalico-webmcp] registered ' + tools.length + ' tools');
    }
    return true;
  }

  // Try once on load. If navigator.modelContext isn't available yet (no EPP, no
  // polyfill loaded yet), poll for ~30s and also listen for a 'webmcp:ready'
  // event so late-loading polyfills (e.g. installed by an extension) can
  // announce themselves without us missing them.
  if (!register()) {
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      if (register() || attempts > 120) clearInterval(iv);
    }, 250);

    var onReady = function () {
      if (register()) {
        clearInterval(iv);
        document.removeEventListener('webmcp:ready', onReady);
      }
    };
    document.addEventListener('webmcp:ready', onReady);
  }
})();
